import { useNavigate, useRoutes, useLocation } from 'react-router-dom';
import { appRoutes } from '@cfg/pageRoutes.tsx';
import { initAnalytics, analytics } from '@tools/analytics.ts';
import { trackPageView } from '@tools/behaviorAnalytics.ts';

import './App.css'; // Asegúrate de que este archivo exista y tenga los estilos necesarios
import '@styles/generalStyles.css'; // Asegúrate de que este archivo exista y tenga los estilos necesarios
import '@styles/variables.css';
import '@styles/utilities.css';

import { useEffect } from 'react';
import { setNavigate } from '@tools/nav/Navigate.tsx';
import { useAppDispatch, useAppSelector } from '@store/store.tsx';
import { fetchSiteInfo, fetchSiteSettings, selectSiteFeatures, selectSiteInfo } from '@store/slices/settingsSlice.tsx';
import { selectProfile } from '@store/slices/userSlice.tsx';
import LayoutResolver from '@components/layout/LayoutResolver.tsx';
import { useApplyBrandingFavicons } from '@features/branding/useApplyBrandingFavicons.ts';
import { useApplySiteColors } from '@features/theme/hooks/useApplySiteColors.tsx';
import { useApplySiteTypography } from '@features/theme/hooks/useApplySiteTypography.ts';
import SafeModeBanner from '@components/SafeModeBanner.tsx';
import { AssistantPageContextProvider, AssistantWidget } from '@features/assistant';
import { FloatingAreaProvider } from './contexts/FloatingAreaContext.tsx';
import { bootLog } from '@tools/bootLog.ts';
import { ScrollToTopOnRouteChange } from '@tools/navigation/ScrollToTop.tsx';
import { PublicSitePreviewProvider } from './contexts/PublicSitePreviewContext.tsx';
import PublicSiteAccessGate from '@components/public/PublicSiteAccessGate.tsx';

bootLog('App module loaded');



/**
 * App.tsx: Layout principal de la aplicación.
 *
 * Este componente proporciona la estructura visual básica (cabecera, contenido, pie de página).
 * Se espera que las secciones de contenido principal se rendericen dinámicamente
 * a través del mecanismo de `mountComponents` (definido en `main.tsx`),
 * el cual busca elementos con atributos `data-path` dentro de este layout.
 *
 * Por ejemplo, para cargar un dashboard en el área de contenido principal,
 * podrías tener un <div data-path="app/dashboard"></div>.
 * Luego, `pageRoutes.tsx` debería tener una entrada en `componentMapping`
 * para "app/dashboard" que apunte al componente React del dashboard.
 *
 * Nota: Este App.tsx no define <Routes> de react-router-dom.
 * El <Router> que envuelve a <App /> en `main.tsx` proporciona contexto de
 * enrutamiento (ej. para hooks como `useNavigate`), pero App.tsx en sí mismo
 * no gestiona el cambio de vistas basado en la URL a través de <Routes>.
 * Cada componente montado vía `data-path` es envuelto en su propio <Router>
 * por la función `mountComponents`.
 */
function App() {
  bootLog('App render start');
  // React-Router genera los elementos según la URL actual
  const element = useRoutes(appRoutes);
  const dispatch = useAppDispatch();
  const userProfile = useAppSelector(selectProfile);
  const siteFeatures = useAppSelector(selectSiteFeatures);
  useApplyBrandingFavicons();
  useApplySiteColors();
  useApplySiteTypography();

  // logInfo('>>> App - element: ', element);

  const navigate = useNavigate();
  const location = useLocation();

  // Determine if user is staff
  const isStaff = Boolean(userProfile?.staff_user_id);
  const hasAssistantLicense = Boolean(siteFeatures?.ia_assistant);
  // console.error('hasAssistantLicense: ', hasAssistantLicense, siteFeatures);

  // Se ejecuta una sola vez
  useEffect(() => {
    setNavigate(navigate);
    dispatch(fetchSiteSettings());
    dispatch(fetchSiteInfo());
  }, [navigate, dispatch]);

  const siteInfo = useAppSelector(selectSiteInfo);
  useEffect(() => {
    if (siteInfo?.trackingToken) {
      initAnalytics(siteInfo.trackingToken);
      if (userProfile?.id) {
        analytics?.identify(userProfile.id.toString());
      }
    }
  }, [siteInfo?.trackingToken, userProfile?.id]);

  useEffect(() => {
    trackPageView(location.pathname, document.title);
  }, [location.pathname]);


  return (
    <PublicSitePreviewProvider>
      <PublicSiteAccessGate>
        <FloatingAreaProvider>
          <AssistantPageContextProvider>
            <LayoutResolver>
              {/* Indicamos el estado de safe mode para explicar la reducción de cargas opcionales */}
              <ScrollToTopOnRouteChange />
              <SafeModeBanner />
              {element}

              {/* AI Assistant Widget - Global */}
              {/* Mostramos el asistente sólo si la licencia ia_asistant está activa para el sitio */}
              {hasAssistantLicense && <AssistantWidget isStaff={isStaff} />}
            </LayoutResolver>
          </AssistantPageContextProvider>
        </FloatingAreaProvider>
      </PublicSiteAccessGate>
    </PublicSitePreviewProvider>
  );
}

export default App;
